import React, { useState } from "react";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { Loader2, Check, X } from "lucide-react";

/**
 * Premium Application Modal — used by SAT Mentor + SAT Business (and optionally Master).
 * Inspired by but rewritten from the SAT Evaluation form.
 */
const ApplicationModal = ({ open, onClose, programKey, programTitle, variant = "personal" }) => {
  const { lang } = useLang();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [f, setF] = useState({
    first_name: "", last_name: "", email: "", phone: "", country: "Schweiz",
    company: "", current_role: "",
    life_situation: "", biggest_obstacle: "", transformation_goal: "", why_now: "",
    readiness_1to10: 7, budget_ok: false, referral_source: "", additional_notes: "",
    team_size: undefined, industry: "", company_challenge: "",
    language: lang,
  });

  const set = (k) => (e) => setF({ ...f, [k]: e?.target?.type === "checkbox" ? e.target.checked : e.target.value });

  if (!open) return null;
  const isBusiness = variant === "business";
  const totalSteps = isBusiness ? 4 : 3;

  const next = () => setStep(Math.min(step + 1, totalSteps));
  const back = () => setStep(Math.max(step - 1, 1));

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/applications", { ...f, program_key: programKey, language: lang });
      setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Fehler beim Senden");
    } finally { setBusy(false); }
  };

  const T = lang === "en" ? {
    title: "Application", subtitle: "A premium programme begins with a real conversation.",
    step1: "About you", step2: "Your story", step3: "Readiness", step4: "Your company",
    fn: "First name", ln: "Last name", em: "Email", ph: "Phone (optional)", co: "Country",
    role: "Current role / occupation", company: "Company", ind: "Industry", team: "Team size",
    ls: "Your current life situation",
    ls_p: "Briefly — what is shaping this chapter of your life?",
    ob: "Biggest obstacle",
    ob_p: "What is keeping you stuck right now?",
    tg: "Your transformation goal",
    tg_p: "What does your life look like in 6 months if this works?",
    wn: "Why now?",
    wn_p: "What changed that this matters today?",
    rd: "Readiness (1–10)",
    bg: "I understand the investment level of this programme and am ready to commit.",
    rs: "How did you find SAT?",
    notes: "Anything else we should know?",
    cc: "Your company's key challenge right now",
    cc_p: "What is the strategic, cultural or human knot to untie?",
    back: "Back", next: "Next", submit: "Submit application",
    done_t: "Thank you.", done_p: "We've received your application. Within 48 hours someone from our team will reach out personally to schedule a confidential clarification call.",
    close: "Close",
  } : {
    title: "Bewerbung", subtitle: "Ein Premium-Programm beginnt mit einem echten Gespräch.",
    step1: "Über dich", step2: "Deine Geschichte", step3: "Bereitschaft", step4: "Dein Unternehmen",
    fn: "Vorname", ln: "Nachname", em: "E-Mail", ph: "Telefon (optional)", co: "Land",
    role: "Aktuelle Tätigkeit / Beruf", company: "Unternehmen", ind: "Branche", team: "Mitarbeitende",
    ls: "Deine aktuelle Lebenssituation",
    ls_p: "Kurz — was prägt dieses Kapitel deines Lebens gerade?",
    ob: "Grösstes Hindernis",
    ob_p: "Was hält dich gerade fest?",
    tg: "Dein Transformationsziel",
    tg_p: "Wo möchtest du in 6 Monaten stehen, wenn dieser Weg gelingt?",
    wn: "Warum jetzt?",
    wn_p: "Was hat sich verändert, dass es heute wichtig ist?",
    rd: "Bereitschaft (1–10)",
    bg: "Ich kenne das Investitions-Niveau dieses Programms und bin bereit, mich zu verpflichten.",
    rs: "Wie hast du SAT gefunden?",
    notes: "Gibt es etwas, das wir wissen sollten?",
    cc: "Die zentrale Herausforderung Ihres Unternehmens",
    cc_p: "Welcher strategische, kulturelle oder menschliche Knoten muss gelöst werden?",
    back: "Zurück", next: "Weiter", submit: "Bewerbung senden",
    done_t: "Danke dir.", done_p: "Wir haben deine Bewerbung erhalten. Innerhalb von 48 Stunden meldet sich jemand aus unserem Team persönlich, um ein vertrauliches Klärungsgespräch zu vereinbaren.",
    close: "Schliessen",
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md" data-testid="application-modal">
      <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#0E1A30]">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 text-white/55 hover:text-white" data-testid="application-close"><X size={20} /></button>

        {done ? (
          <div className="p-10 text-center" data-testid="application-success">
            <div className="mx-auto w-14 h-14 rounded-full bg-[#D4AF37] text-[#081328] flex items-center justify-center mb-5"><Check size={26} /></div>
            <h3 className="serif text-3xl font-light">{T.done_t}</h3>
            <p className="text-white/70 mt-4 leading-relaxed">{T.done_p}</p>
            <button onClick={onClose} className="btn-secondary mt-8">{T.close}</button>
          </div>
        ) : (
          <div className="p-7 md:p-10">
            <div className="overline">{T.title}</div>
            <h3 className="serif text-2xl md:text-3xl mt-1 font-light leading-tight">{programTitle}</h3>
            <p className="text-white/55 text-sm mt-2">{T.subtitle}</p>

            {/* Step indicator */}
            <div className="flex gap-1.5 mt-6">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div key={i} className={`h-0.5 flex-1 ${i < step ? "bg-[#D4AF37]" : "bg-white/10"}`} />
              ))}
            </div>

            <div className="mt-6 space-y-4">
              {step === 1 && (
                <>
                  <div className="overline mb-2">{T.step1}</div>
                  <div className="grid grid-cols-2 gap-3">
                    <input required className="sat-input" placeholder={T.fn} value={f.first_name} onChange={set("first_name")} data-testid="app-first-name" />
                    <input className="sat-input" placeholder={T.ln} value={f.last_name} onChange={set("last_name")} />
                  </div>
                  <input required type="email" className="sat-input" placeholder={T.em} value={f.email} onChange={set("email")} data-testid="app-email" />
                  <div className="grid grid-cols-2 gap-3">
                    <input className="sat-input" placeholder={T.ph} value={f.phone} onChange={set("phone")} />
                    <input className="sat-input" placeholder={T.co} value={f.country} onChange={set("country")} />
                  </div>
                  <input className="sat-input" placeholder={T.role} value={f.current_role} onChange={set("current_role")} />
                </>
              )}

              {step === 2 && (
                <>
                  <div className="overline mb-2">{T.step2}</div>
                  <label className="text-sm text-white/70">{T.ls}</label>
                  <textarea rows={3} className="sat-input" placeholder={T.ls_p} value={f.life_situation} onChange={set("life_situation")} data-testid="app-life" />
                  <label className="text-sm text-white/70 mt-2 block">{T.ob}</label>
                  <textarea rows={3} className="sat-input" placeholder={T.ob_p} value={f.biggest_obstacle} onChange={set("biggest_obstacle")} />
                  <label className="text-sm text-white/70 mt-2 block">{T.tg}</label>
                  <textarea rows={3} className="sat-input" placeholder={T.tg_p} value={f.transformation_goal} onChange={set("transformation_goal")} />
                  <label className="text-sm text-white/70 mt-2 block">{T.wn}</label>
                  <textarea rows={2} className="sat-input" placeholder={T.wn_p} value={f.why_now} onChange={set("why_now")} />
                </>
              )}

              {step === 3 && (
                <>
                  <div className="overline mb-2">{T.step3}</div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white/70">{T.rd}</span>
                      <span className="text-[#D4AF37] font-semibold">{f.readiness_1to10}</span>
                    </div>
                    <input type="range" min="1" max="10" value={f.readiness_1to10} onChange={(e) => setF({...f, readiness_1to10: +e.target.value})} className="w-full accent-[#D4AF37]" data-testid="app-readiness" />
                  </div>
                  <label className="flex items-start gap-3 text-sm text-white/75 cursor-pointer">
                    <input type="checkbox" checked={!!f.budget_ok} onChange={set("budget_ok")} className="mt-1" data-testid="app-budget" />
                    <span>{T.bg}</span>
                  </label>
                  <input className="sat-input" placeholder={T.rs} value={f.referral_source} onChange={set("referral_source")} />
                  <textarea rows={3} className="sat-input" placeholder={T.notes} value={f.additional_notes} onChange={set("additional_notes")} />
                </>
              )}

              {step === 4 && isBusiness && (
                <>
                  <div className="overline mb-2">{T.step4}</div>
                  <input className="sat-input" placeholder={T.company} value={f.company} onChange={set("company")} data-testid="app-company" />
                  <div className="grid grid-cols-2 gap-3">
                    <input className="sat-input" placeholder={T.ind} value={f.industry} onChange={set("industry")} />
                    <input type="number" min="1" className="sat-input" placeholder={T.team} value={f.team_size || ""} onChange={(e) => setF({...f, team_size: e.target.value ? +e.target.value : undefined})} />
                  </div>
                  <label className="text-sm text-white/70 mt-2 block">{T.cc}</label>
                  <textarea rows={4} className="sat-input" placeholder={T.cc_p} value={f.company_challenge} onChange={set("company_challenge")} />
                </>
              )}
            </div>

            {err && <p className="text-red-300 text-sm mt-4">{err}</p>}

            <div className="flex justify-between mt-7 gap-3">
              <button onClick={back} disabled={step === 1} className="btn-ghost text-sm disabled:opacity-30" data-testid="app-back">{T.back}</button>
              {step < totalSteps ? (
                <button onClick={next} disabled={(step === 1 && (!f.first_name || !f.email))} className="btn-primary text-sm disabled:opacity-40" data-testid="app-next">{T.next}</button>
              ) : (
                <button onClick={submit} disabled={busy} className="btn-primary text-sm" data-testid="app-submit">
                  {busy ? <Loader2 className="animate-spin" size={16} /> : T.submit}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationModal;
